/**
 * 組件註冊表 - 使用 import.meta.glob 預載入所有動態組件
 * 解決 Capacitor 打包後動態 import 無法運作的問題
 */

import type { Component } from 'vue';

// ============================================================
// 類型定義
// ============================================================

/** 組件載入器類型 */
type ComponentLoader = () => Promise<{ default: Component } | Component>;

/** 組件映射表類型 */
type ComponentMap = Record<string, ComponentLoader>;

/** 組件註冊表 API */
export interface ComponentRegistryAPI {
    loadComponentByPath: typeof loadComponentByPath;
    hasComponent: typeof hasComponent;
    getRegisteredComponents: typeof getRegisteredComponents;
}

// ============================================================
// 組件載入
// ============================================================

// Operations 模組 (legacy)
const operationsComponents: ComponentMap = import.meta.glob('/apps/operations/**/*.vue');

// PPOM 模組 (只有根目錄的組件)
const ppomComponents: ComponentMap = import.meta.glob('/apps/ppom/*.vue');

// Admin 模組
const adminComponents: ComponentMap = import.meta.glob('/apps/admin/**/*.vue');

// Bigscreen 模組
const bigscreenComponents: ComponentMap = import.meta.glob('/apps/bigscreen/**/*.vue');

// Chat 模組
const chatComponents: ComponentMap = import.meta.glob('/apps/chat/**/*.vue');

// Planner 模組
const plannerComponents: ComponentMap = import.meta.glob('/apps/planner/**/*.vue');

// Warroom 模組
const warroomComponents: ComponentMap = import.meta.glob('/apps/warroom/**/*.vue');

// Core 本地模組 - Field 案場相關
const fieldComponents: ComponentMap = import.meta.glob('/src/views/field/**/*.vue');

// Core 本地模組 - Data 設備資料相關
const dataComponents: ComponentMap = import.meta.glob('/src/views/data/**/*.vue');

// Core 本地模組 - Detail 詳情頁面
const detailComponents: ComponentMap = import.meta.glob('/src/views/detail/**/*.vue');

// Core 本地模組 - Tools 工具
const toolsComponents: ComponentMap = import.meta.glob('/src/views/tools/**/*.vue');

// Core 本地模組 - Operations 維運（開發中）
const operationsLocalComponents: ComponentMap = import.meta.glob('/src/views/operations/**/*.vue');

// 合併所有組件
const allComponents: ComponentMap = {
    ...operationsComponents,
    ...ppomComponents,
    ...adminComponents,
    ...bigscreenComponents,
    ...chatComponents,
    ...plannerComponents,
    ...warroomComponents,
    ...fieldComponents,
    ...dataComponents,
    ...detailComponents,
    ...toolsComponents,
    ...operationsLocalComponents,
};

// ============================================================
// 公開函數
// ============================================================

/**
 * 根據路徑載入組件
 * @param componentPath - 組件路徑，如 '/apps/operations/patrol/SystemOverview.vue'
 * @returns 組件載入 Promise，若找不到則返回 null
 */
export function loadComponentByPath(componentPath: string): Promise<Component | null> {
    const loader = allComponents[componentPath];
    if (loader) {
        return loader().then((module) => {
            // 處理 ES module default export
            if (module && typeof module === 'object' && 'default' in module) {
                return module.default as Component;
            }
            return module as Component;
        });
    }
    console.error(`[componentRegistry] 找不到組件: ${componentPath}`);
    return Promise.resolve(null);
}

/**
 * 檢查組件是否存在
 * @param componentPath - 組件路徑
 * @returns 是否存在該組件
 */
export function hasComponent(componentPath: string): boolean {
    return !!allComponents[componentPath];
}

/**
 * 取得所有已註冊的組件路徑
 * @returns 已註冊的組件路徑陣列
 */
export function getRegisteredComponents(): string[] {
    return Object.keys(allComponents);
}

// ============================================================
// 預設匯出
// ============================================================

const componentRegistry: ComponentRegistryAPI = {
    loadComponentByPath,
    hasComponent,
    getRegisteredComponents,
};

export default componentRegistry;
